/**
 * Typed wrappers for the Shift + Roster + Swap module (migration 057).
 * Paths + shapes are taken VERBATIM from backend/internal/router/router.go
 * and handlers/shifts.go. Same auth/CSRF/feature-gate posture as
 * lib/api/attendance.ts (RequireFeature("time_tracking")).
 */

import { api } from "./client";
import type {
  CreateShiftRequest,
  CreateSwapRequest,
  DecisionRequest,
  ListRosterResponse,
  ListShiftsResponse,
  ListSwapsResponse,
  RosterAssignment,
  RosterUpsertItem,
  Shift,
  ShiftSwap,
  UpdateShiftRequest,
} from "@/types/attendance";

export const SHIFTS_BASE = "/api/v1/shifts";

// --- shift definitions ---

export async function listShifts(
  params?: { include_inactive?: boolean },
  signal?: AbortSignal,
): Promise<ListShiftsResponse> {
  const { data } = await api.get<ListShiftsResponse>(SHIFTS_BASE, {
    params,
    signal,
  });
  return data;
}

export async function createShift(body: CreateShiftRequest): Promise<Shift> {
  const { data } = await api.post<Shift>(SHIFTS_BASE, body);
  return data;
}

export async function updateShift(
  id: number,
  body: UpdateShiftRequest,
): Promise<Shift> {
  const { data } = await api.patch<Shift>(`${SHIFTS_BASE}/${id}`, body);
  return data;
}

export async function deleteShift(id: number): Promise<void> {
  await api.delete(`${SHIFTS_BASE}/${id}`);
}

// --- roster ---

export async function listRoster(
  params?: { from?: string; to?: string; date?: string; employee_id?: number },
  signal?: AbortSignal,
): Promise<ListRosterResponse> {
  const { data } = await api.get<ListRosterResponse>(`${SHIFTS_BASE}/roster`, {
    params,
    signal,
  });
  return data;
}

// PUT /shifts/roster — bulk upsert of (employee, date) -> shift.
export async function upsertRoster(
  assignments: RosterUpsertItem[],
): Promise<{ roster: RosterAssignment[] }> {
  const { data } = await api.put<{ roster: RosterAssignment[] }>(
    `${SHIFTS_BASE}/roster`,
    { assignments },
  );
  return data;
}

export async function deleteRoster(id: number): Promise<void> {
  await api.delete(`${SHIFTS_BASE}/roster/${id}`);
}

// --- swaps ---

export async function createSwap(body: CreateSwapRequest): Promise<ShiftSwap> {
  const { data } = await api.post<ShiftSwap>(`${SHIFTS_BASE}/swaps`, body);
  return data;
}

// `exclude_self=1` narrows an approver's list to swaps they can ACT on
// (own swap requests removed).
export async function listSwaps(
  params?: { status?: string; scope?: "self"; exclude_self?: boolean },
  signal?: AbortSignal,
): Promise<ListSwapsResponse> {
  const { exclude_self, ...rest } = params ?? {};
  const { data } = await api.get<ListSwapsResponse>(`${SHIFTS_BASE}/swaps`, {
    params: { ...rest, ...(exclude_self ? { exclude_self: 1 } : {}) },
    signal,
  });
  return data;
}

export async function acceptSwap(id: number): Promise<ShiftSwap> {
  const { data } = await api.post<ShiftSwap>(
    `${SHIFTS_BASE}/swaps/${id}/accept`,
    {},
  );
  return data;
}

export async function approveSwap(
  id: number,
  body?: DecisionRequest,
): Promise<ShiftSwap> {
  const { data } = await api.post<ShiftSwap>(
    `${SHIFTS_BASE}/swaps/${id}/approve`,
    body ?? {},
  );
  return data;
}

export async function rejectSwap(
  id: number,
  body?: DecisionRequest,
): Promise<ShiftSwap> {
  const { data } = await api.post<ShiftSwap>(
    `${SHIFTS_BASE}/swaps/${id}/reject`,
    body ?? {},
  );
  return data;
}

export async function cancelSwap(id: number): Promise<ShiftSwap> {
  const { data } = await api.post<ShiftSwap>(
    `${SHIFTS_BASE}/swaps/${id}/cancel`,
    {},
  );
  return data;
}
